"use client"

import { useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

import { Calendar, Star, ArrowRight, Clock, Leaf } from "lucide-react"
import Link from "next/link"
import { SearchHighlight } from "@/components/ui/search-highlight"
import { getCanonicalImageUrl } from "@/lib/directus"
import { resolveBlogImageAlt } from "@/lib/blog-image-alt"
import { generateBlurDataURL, getOptimizedImageSizes, getImageDimensions, directusImageLoader } from "@/lib/image-utils"
import { categoryToSlug, getCategoryColor } from "@/lib/category-utils"
import Image from "next/image"
import { BRAND_ASSET_PATHS } from "@/lib/brand-assets"
import "./blog-card.css"

interface BlogCardProps {
  post: {
    id: string
    title: string
    excerpt: string
    category: string
    date?: string
    date_published?: string
    image: string
    image_alt?: string | null
    featured?: boolean
    slug?: string
    reading_time?: number
  }
  index?: number
  variant?: 'featured' | 'regular' | 'compact'
  searchTerm?: string // Add search term for highlighting
  priority?: 'high' | 'medium' | 'low' // For timeline page priority-based styling
  viewMode?: "grid" | "list" // Grid or list view layout
  imagePriority?: boolean // For eager loading above-the-fold images
  hideCategory?: boolean // Hide category badge (useful on category pages where it's redundant)
}

export function BlogCard({ post, index = 0, variant = 'regular', searchTerm = "", priority, viewMode = "grid", imagePriority = false, hideCategory = false }: BlogCardProps) {
  const isFeatured = variant === 'featured'
  const isCompact = variant === 'compact'
  
  // Truncate excerpt for consistency - increased length for better educational previews
  const truncateExcerpt = (text: string, maxLength: number = 240) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  
  // Better date formatting
  // timeZone: 'UTC' pins the format so SSR (UTC) and CSR (user TZ) produce
  // identical text for posts published near UTC midnight — same TZ-trap class
  // as the existing isNew gate below.
  const formatDate = (date: string) => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      });
    } catch (error) {
      return 'Date unavailable';
    }
  };

  // "New" badge fires for articles published within the last 14 days.
  // Gated on hasMounted: with ISR, server-rendered HTML can sit in cache for up
  // to 24h while the client's clock keeps moving. Computing "isNew" during SSR
  // and again during hydration would disagree for posts crossing the 14-day
  // boundary, producing React hydration error #418.
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isNew = useMemo(() => {
    if (!hasMounted) return false
    if (!post.date_published && !post.date) return false
    try {
      const publishedDate = new Date(post.date_published || post.date || '')
      const now = new Date()
      const daysDiff = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24)
      return daysDiff >= 0 && daysDiff <= 14
    } catch {
      return false
    }
  }, [hasMounted, post.date_published, post.date])

  const categoryColor = getCategoryColor(post.category)

  
  // List view layout
  if (viewMode === "list") {
    return (
      <Card
        className={`blog-card bg-white/95 backdrop-blur-xs border-l-4 ${
          isFeatured ? 'border-l-yellow-400 border-yellow-200' : 'border-l-green-500 border-gray-200'
        } border-r border-t border-b shadow-lg hover:shadow-xl hover:border-l-4 ${
          isFeatured ? 'hover:border-l-yellow-500' : 'hover:border-l-green-600'
        } transition-[box-shadow,border-color,border-left-width] duration-300 ease-out overflow-hidden group card-hover flex flex-row focus-within:outline-2 focus-within:outline-green-500 focus-within:outline-offset-2 sm:min-h-[160px] md:min-h-[200px] relative cursor-pointer`}
        role="article"
      >
        {/* Image - Left side with vertical padding */}
        <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-44 lg:h-44 flex-shrink-0 relative my-2 sm:my-3 md:my-4 ml-2 sm:ml-3 md:ml-4">
          <div className="w-full h-full bg-linear-to-br from-gray-50 to-gray-100 rounded-lg sm:rounded-xl overflow-hidden shadow-md border border-gray-200 relative">
            {/* Featured badge only - New indicator moved to date */}
            {isFeatured && (
              <div className="absolute top-1.5 left-1.5 z-30">
                <Badge className="bg-linear-to-r from-yellow-400 to-orange-400 text-white font-bold text-[9px] px-1.5 py-0.5 sm:text-[10px] sm:px-2 sm:py-0.5 shadow-lg border-0">
                  <Star className="w-2 h-2 sm:w-2.5 sm:h-2.5 mr-0.5" />
                  Featured
                </Badge>
              </div>
            )}
            {post.image ? (
              <Image
                src={getCanonicalImageUrl(post.image, post.slug || post.title)}
                loader={directusImageLoader}
                alt={resolveBlogImageAlt(post)}
                width={192}
                height={192}
                sizes="(max-width: 640px) 96px, (max-width: 768px) 128px, 192px"
                placeholder="blur"
                blurDataURL={generateBlurDataURL(post.image)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                priority={imagePriority}
                loading={imagePriority ? undefined : "lazy"}
              />
            ) : (
              <div className="w-full h-full bg-linear-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                <Leaf className="w-8 h-8 text-green-600" />
              </div>
            )}
            {/* Subtle overlay on hover */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300"></div>
          </div>
        </div>

        {/* Content - Right side */}
        <div className="flex-1 flex flex-col justify-between p-2 sm:p-3 md:p-4 min-w-0 relative">
          {/* Top content - Title & Excerpt */}
          <div className="pr-6 sm:pr-0 sm:flex-1">
            {/* Title */}
            <CardTitle className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-green-700 transition-colors mb-0.5 sm:mb-1.5">
              <Link
                href={`/blog/${post.slug || post.id}`}
                className="hover:text-green-700 transition-colors after:absolute after:inset-0 after:z-10"
              >
                <SearchHighlight
                  text={post.title}
                  searchTerm={searchTerm}
                  className="text-sm sm:text-base md:text-lg lg:text-xl font-bold leading-tight"
                />
              </Link>
            </CardTitle>

            {/* Excerpt */}
            <p className="text-gray-600 text-xs sm:text-sm md:text-base leading-snug sm:leading-relaxed line-clamp-2 mb-1.5 sm:mb-0">
              <SearchHighlight
                text={truncateExcerpt(post.excerpt, 200)}
                searchTerm={searchTerm}
                className="text-gray-600 text-xs sm:text-sm md:text-base leading-snug sm:leading-relaxed"
              />
            </p>
          </div>

          {/* Metadata + CTA inline row - bottom of card */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-1.5 flex-wrap text-[10px] sm:text-xs text-gray-500">
              <Link href={`/blog/category/${categoryToSlug(post.category)}`} prefetch={false} className="relative z-20">
                <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-200 font-medium text-[9px] sm:text-[10px] px-1.5 py-0 rounded transition-colors cursor-pointer">
                  {post.category}
                </Badge>
              </Link>
              <span className="text-gray-300">•</span>
              <span className="flex items-center gap-0.5">
                <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                <span>{post.reading_time || 5} min</span>
              </span>
              <span className="text-gray-300 hidden sm:inline">•</span>
              <span className={`whitespace-nowrap hidden sm:inline ${isNew ? 'text-green-600 font-medium' : ''}`}>
                {formatDate(post.date_published || post.date || '')}
              </span>
            </div>

            {/* Desktop CTA */}
            <div className="hidden sm:block relative z-20 flex-shrink-0">
              <Link
                href={`/blog/${post.slug || post.id}`}
                className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold text-xs md:text-sm transition-colors group/btn whitespace-nowrap"
              >
                Read More
                <span className="sr-only"> about {post.title}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 flex-shrink-0 group-hover/btn:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>

            {/* Mobile tap indicator */}
            <div className="sm:hidden flex-shrink-0">
              <ArrowRight className="w-4 h-4 text-green-600" />
            </div>
          </div>
        </div>
      </Card>
    )
  }
  
  // Compact variant - matches list view styling on mobile
  if (isCompact) {
    return (
      <Card
        className="blog-card ds-glass hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 ease-out overflow-hidden group card-hover focus-within:outline-2 focus-within:outline-green-500 focus-within:outline-offset-2 relative cursor-pointer flex flex-row items-center sm:items-stretch sm:flex-col sm:h-full min-h-[112px] sm:min-h-0"
        role="article"
      >
        {/* Image - square on mobile, aspect-video on tablet+ */}
        <div className="w-[88px] h-[88px] sm:w-full sm:h-auto sm:aspect-video flex-shrink-0 overflow-hidden relative ml-3 sm:m-0 rounded-lg sm:rounded-none">
          {/* New badge */}
          {isNew && (
            <div className="absolute top-1 left-1 sm:top-2 sm:left-2 z-10">
              <Badge className="bg-white/90 text-green-700 font-semibold text-[10px] sm:text-xs border border-green-200 px-1 sm:px-2 py-0.5 backdrop-blur-xs">
                <Star className="w-2 h-2 sm:w-3 sm:h-3 mr-0.5" />
                New
              </Badge>
            </div>
          )}
          {post.image ? (
            <Image
              src={getCanonicalImageUrl(post.image, post.slug || post.title)}
              loader={directusImageLoader}
              alt={resolveBlogImageAlt(post)}
              width={getImageDimensions('card').width}
              height={getImageDimensions('card').height}
              placeholder="blur"
              blurDataURL={generateBlurDataURL(post.image)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              priority={imagePriority}
              loading={imagePriority ? undefined : "lazy"}
              sizes={getOptimizedImageSizes('card')}
            />
          ) : (
            <div className="w-full h-full bg-linear-to-br from-green-100 to-emerald-100 flex items-center justify-center">
              <Leaf className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col py-3 px-3 sm:p-4 min-w-0 relative">
          {/* Title - full title shown, no truncation */}
          <CardTitle className="text-sm sm:text-base md:text-lg font-bold text-green-700 leading-tight group-hover:text-green-800 transition-colors mb-1 sm:mb-2 pr-8 sm:pr-0">
            <Link
              href={`/blog/${post.slug || post.id}`}
              className="hover:text-green-800 transition-colors after:absolute after:inset-0 after:z-10"
            >
              <SearchHighlight
                text={post.title}
                searchTerm={searchTerm}
                className="text-sm sm:text-base md:text-lg font-bold leading-tight"
              />
            </Link>
          </CardTitle>

          {/* Excerpt */}
          <p className="text-gray-600 text-xs sm:text-sm leading-snug line-clamp-2 mb-1.5 sm:mb-2 pr-8 sm:pr-0">
            <SearchHighlight
              text={truncateExcerpt(post.excerpt, 100)}
              searchTerm={searchTerm}
              className="text-gray-600 text-xs sm:text-sm leading-snug"
            />
          </p>

          {/* Arrow - vertically centered on right (mobile only) */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 sm:hidden">
            <ArrowRight className="w-4 h-4 text-green-600" />
          </div>

          {/* Footer: metadata + CTA on same row */}
          <div className="sm:mt-auto flex items-center justify-between gap-2">
            {/* Metadata - reading time only (category and date dropped on compact for space) */}
            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-gray-500 min-w-0">
              <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
              <span className="shrink-0">{post.reading_time || 5} min</span>
            </div>

            {/* CTA - tablet+ only, same row */}
            <Link
              href={`/blog/${post.slug || post.id}`}
              className="hidden sm:inline-flex items-center text-green-600 hover:text-green-700 font-medium text-sm group/link transition-colors relative z-20 flex-shrink-0"
            >
              Read article
              <span className="sr-only"> about {post.title}</span>
              <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover/link:translate-x-0.5 transition-transform duration-200" />
            </Link>
          </div>
        </div>
      </Card>
    )
  }

  // Regular/Featured variant - vertical layout
  // Whole card is the link (anchor wraps content). Category color is carried by
  // the dot beside the category label; "Read article →" reveals on hover.
  return (
    <Card
      className={`blog-card bg-white border border-gray-200 hover:border-gray-300 shadow-xs hover:shadow-xl transition-[transform,box-shadow,border-color] duration-300 ease-out transform hover:-translate-y-1 overflow-hidden group h-full flex flex-col focus-within:outline-2 focus-within:outline-green-500 focus-within:outline-offset-2 relative cursor-pointer ${
        isFeatured ? 'border-yellow-200' : ''
      }`}
      role="article"
    >
      <Link
        href={`/blog/${post.slug || post.id}`}
        aria-label={post.title}
        className="absolute inset-0 z-10"
      >
        <span className="sr-only">{post.title}</span>
      </Link>

      {isFeatured && (
        <div className="absolute top-4 left-4 z-20">
          <Badge className="bg-linear-to-r from-yellow-400 to-orange-400 text-white font-bold text-xs md:text-sm shadow-lg border-0 px-3 py-1.5">
            <Star className="w-3 h-3 mr-1" />
            Featured
          </Badge>
        </div>
      )}

      {isNew && !isFeatured && (
        <div className="absolute top-4 left-4 z-20">
          <Badge className="bg-white/90 backdrop-blur-xs text-gray-800 font-semibold text-[10px] md:text-xs tracking-wide border border-gray-200 px-2 py-0.5 shadow-xs gap-1.5">
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-green-600" />
            New
          </Badge>
        </div>
      )}

      {isNew && isFeatured && (
        <div className="absolute top-4 right-4 z-20">
          <Badge className="bg-white/90 backdrop-blur-xs text-gray-800 font-semibold text-[10px] md:text-xs tracking-wide border border-gray-200 px-2 py-0.5 shadow-xs gap-1.5">
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-green-600" />
            New
          </Badge>
        </div>
      )}

      <div className="aspect-video overflow-hidden relative">
        {post.image ? (
          <Image
            src={getCanonicalImageUrl(post.image, post.slug || post.title)}
            loader={directusImageLoader}
            alt={resolveBlogImageAlt(post)}
            width={getImageDimensions('card').width}
            height={getImageDimensions('card').height}
            placeholder="blur"
            blurDataURL={generateBlurDataURL(post.image)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            priority={imagePriority}
            loading={imagePriority ? undefined : "lazy"}
            sizes={getOptimizedImageSizes('card')}
          />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-green-100 to-emerald-100 flex items-center justify-center">
            <div className="text-center">
              <Image
                src={BRAND_ASSET_PATHS.iconDefault}
                alt="What Grass Is This? Logo"
                width={32}
                height={32}
                className="mx-auto mb-2 h-8 w-8"
              />
              <p className="text-green-700 text-sm font-medium">Lawn Care Tips</p>
            </div>
          </div>
        )}
      </div>

      <CardHeader className="p-5 md:p-6 pb-3">
        {!hideCategory && (
          <div className="flex items-center gap-2 mb-3 flex-wrap relative z-20">
            <Link
              href={`/blog/category/${categoryToSlug(post.category)}`}
              prefetch={false}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-green-700 transition-colors"
            >
              <span
                aria-hidden="true"
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: categoryColor }}
              />
              {post.category}
            </Link>
          </div>
        )}

        {/* Title — foreground gray-900 by default. Brand green is reserved for hover/CTA. */}
        <CardTitle className="text-xl md:text-2xl mb-3 leading-tight font-bold text-gray-900 group-hover:text-green-700 transition-colors">
          <SearchHighlight
            text={post.title}
            searchTerm={searchTerm}
            className="text-xl md:text-2xl leading-tight font-bold"
          />
        </CardTitle>

        <div className="flex items-center text-xs text-gray-500">
          <Calendar className="w-3 h-3 mr-1" />
          <span>{formatDate(post.date_published || post.date || '')}</span>
          {post.reading_time && (
            <>
              <span className="mx-2">•</span>
              <Clock className="w-3 h-3 mr-1" />
              <span>{post.reading_time} min read</span>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className="px-5 md:px-6 pb-5 flex flex-col grow">
        <p className="text-gray-600 leading-relaxed text-sm md:text-base mb-3 line-clamp-3 grow">
          <SearchHighlight
            text={truncateExcerpt(post.excerpt, 200)}
            searchTerm={searchTerm}
            className="text-gray-600 leading-relaxed text-sm md:text-base"
          />
        </p>

        {/* Hover-reveal affordance. Reserves 22px so cards don't jump on hover. */}
        <div
          aria-hidden="true"
          className="mt-auto h-[22px] flex items-center gap-1.5 text-sm font-semibold text-green-700 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200"
        >
          Read article
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </CardContent>
    </Card>
  )
}
